import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { refreshPlatformAccessToken } from '@/lib/platform-token-refresh';

export const platformApiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
  withCredentials: true,
});

// ── Request: attach current access token ────────────────────────────────────
platformApiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!config.headers.has('Authorization')) {
    const token = usePlatformAuthStore.getState().accessToken;
    if (token !== null) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
  }
  return config;
});

// ── Response: unwrap { data, meta } envelope ─────────────────────────────────
platformApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const body = response.data as Record<string, unknown> | null | undefined;
    if (body !== null && body !== undefined && typeof body === 'object' && 'data' in body) {
      return { ...response, data: body['data'] };
    }
    return response;
  },
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }

    const config = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    // The login endpoint's own 401 means "invalid credentials," not "session
    // expired" — there is no session yet to refresh. Reject with the original
    // AxiosError untouched so callers (e.g. PlatformLoginPage) can still see
    // `err.response.status === 401` instead of a masked generic error.
    if (config?.url?.includes('/platform/auth/login')) {
      return Promise.reject(error);
    }

    // Guard: don't retry if already retried, or if this IS the refresh endpoint
    if (!config || config._retry || config.url?.includes('/platform/auth/refresh')) {
      usePlatformAuthStore.getState().clearAuth();
      return Promise.reject(new Error('Session expired. Please sign in again.'));
    }

    // Optimistic check: if the token stored now differs from what was sent, another
    // mechanism (proactive timer) already refreshed. Retry immediately with the new token.
    const currentToken = usePlatformAuthStore.getState().accessToken;
    const sentToken = (config.headers.get('Authorization') as string | undefined)?.replace(
      'Bearer ',
      '',
    );

    if (currentToken && sentToken && currentToken !== sentToken) {
      config._retry = true;
      config.headers.set('Authorization', `Bearer ${currentToken}`);
      return platformApiClient(config);
    }

    // Attempt refresh
    config._retry = true;
    try {
      const newToken = await refreshPlatformAccessToken();
      config.headers.set('Authorization', `Bearer ${newToken}`);
      return platformApiClient(config);
    } catch {
      // refreshPlatformAccessToken already called clearAuth() on failure
      return Promise.reject(new Error('Session expired. Please sign in again.'));
    }
  },
);
